// Payment validators

import { z } from 'zod';
import { dateSchema, paymentMethodEnumSchema, paymentStatusEnumSchema } from '../utils/validation';

// Create payment validator
export const createPaymentSchema = z.object({
  invoiceId: z.string().min(1, 'Invoice ID is required'),
  amount: z.number().positive('Payment amount must be positive'),
  paymentDate: dateSchema,
  paymentMethod: paymentMethodEnumSchema,
  transactionId: z.string().optional(),
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
});

// Update payment validator
export const updatePaymentSchema = z.object({
  amount: z.number().positive('Payment amount must be positive').optional(),
  paymentDate: dateSchema.optional(),
  paymentMethod: paymentMethodEnumSchema.optional(),
  transactionId: z.string().optional(),
  status: paymentStatusEnumSchema.optional(),
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
  proofUrl: z.string().url('Must be a valid URL').nullable().optional(),
  processingFee: z.number().min(0, 'Processing fee cannot be negative').optional(),
});

// Payment filter validator
export const paymentFilterSchema = z.object({
  invoiceId: z.string().optional(),
  customerId: z.string().optional(),
  paymentMethod: paymentMethodEnumSchema.optional(),
  status: paymentStatusEnumSchema.optional(),
  dateFrom: dateSchema.optional(),
  dateTo: dateSchema.optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(100).optional(),
  sortBy: z.enum(['paymentDate', 'amount', 'createdAt']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

// Payment method validator
export const paymentMethodSchema = z.object({
  type: z.enum(['cash', 'bank_transfer', 'check', 'credit_card', 'pph_withholding', 'other']),
  details: z.record(z.unknown()).optional(),
  isDefault: z.boolean().optional(),
});

// Multi-payment validator (for paying multiple invoices at once)
export const multiPaymentSchema = z.object({
  payments: z
    .array(
      z.object({
        invoiceId: z.string().min(1, 'Invoice ID is required'),
        amount: z.number().positive('Payment amount must be positive'),
      })
    )
    .min(1, 'At least one payment is required'),
  paymentDate: dateSchema,
  paymentMethod: paymentMethodEnumSchema,
  transactionId: z.string().optional(),
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
});

// Payment reconciliation validator
export const paymentReconciliationSchema = z.object({
  startDate: dateSchema,
  endDate: dateSchema,
  customerId: z.string().optional(),
  paymentMethod: paymentMethodEnumSchema.optional(),
});

// Payment refund validator
export const paymentRefundSchema = z.object({
  paymentId: z.string().min(1, 'Payment ID is required'),
  amount: z.number().positive('Refund amount must be positive'),
  refundDate: dateSchema.optional(),
  reason: z
    .string()
    .min(10, 'Refund reason must be at least 10 characters')
    .max(500, 'Refund reason cannot exceed 500 characters'),
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
});

// Payment reporting validator
export const paymentReportSchema = z.object({
  dateFrom: dateSchema,
  dateTo: dateSchema,
  groupBy: z.enum(['day', 'week', 'month', 'year']).optional(),
  customerId: z.string().optional(),
  paymentMethod: paymentMethodEnumSchema.optional(),
  format: z.enum(['json', 'csv', 'pdf']).optional(),
});

// Types
export type CreatePaymentInput = z.infer<typeof createPaymentSchema>;
export type UpdatePaymentInput = z.infer<typeof updatePaymentSchema>;
export type PaymentFilterInput = z.infer<typeof paymentFilterSchema>;
export type PaymentMethodInput = z.infer<typeof paymentMethodSchema>;
export type MultiPaymentInput = z.infer<typeof multiPaymentSchema>;
export type PaymentReconciliationInput = z.infer<typeof paymentReconciliationSchema>;
export type PaymentRefundInput = z.infer<typeof paymentRefundSchema>;
export type PaymentReportInput = z.infer<typeof paymentReportSchema>;
